import React from "react";
import type { DataType } from "./detect";
import {
  detectDataType,
  toEpochMs,
  isParsableDateStr,
} from "./detect";

/* ------------------------------------------------------------------
   Pretty-print any cell value
------------------------------------------------------------------ */
export function formatValue(
  value: unknown,
  columnName?: string,
  forcedType?: DataType,
  duckType?: string,
): React.ReactNode {
  /* nullish */
  if (value === null || value === undefined) {
    return <span className="italic text-muted-foreground">null</span>;
  }

  /* decide type */
  const type = forcedType ?? detectDataType(value, columnName, duckType);

  /* DATE ---------------------------------------------------------------- */
  if (type === "date") {
    let date: Date | null = null;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "number" || typeof value === "bigint") {
      const ms = toEpochMs(value);
      if (ms !== null) date = new Date(ms);
    } else if (typeof value === "string" && isParsableDateStr(value)) {
      const ms = Date.parse(value);
      if (!isNaN(ms)) date = new Date(ms);
    }

    if (!date || isNaN(date.getTime())) {
      return <span className="text-lg font-medium">{String(value)}</span>;
    }

    return (
      <span className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
        {date.toLocaleString()}
      </span>
    );
  }

  /* NUMBER -------------------------------------------------------------- */
  if (type === "number") {
    // Normalize BigInt to Number
    const n = typeof value === "bigint" ? Number(value) : (value as number);
    const abs = Math.abs(n);

    let formatted: string;
    if (Number.isInteger(abs)) {
      // If a four-digit integer, skip the comma
      if (abs >= 1000 && abs <= 9999) {
        formatted = abs.toString();
      } else {
        formatted = abs.toLocaleString();
      }
    } else {
      // Non-integers keep two-to-four fraction digits
      formatted = abs.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    }

    const neg = n < 0;
    const display = neg ? `(${formatted})` : formatted;
    const className = neg
      ? "text-lg font-semibold text-red-600 dark:text-red-300"
      : "text-lg font-semibold text-emerald-800 dark:text-emerald-300";

    return <span className={className}>{display}</span>;
  }

  /* BOOLEAN ------------------------------------------------------------- */
  if (type === "boolean") {
    return (
      <span className={`text-lg font-medium ${value ? "text-green-700" : "text-red-600"}`}>
        {String(value)}
      </span>
    );
  }

  /* STRING (truncate) --------------------------------------------------- */
  const s = String(value);
  if (s.length > 150) {
    return (
      <span className="text-lg font-medium" title={s}>
        {s.slice(0, 147)}…
      </span>
    );
  }
  return <span className="text-lg font-medium">{s}</span>;
}
